import React, { useContext, useEffect, useRef, useState } from "react";
import styles from "./style.module.css";
import {
  FaAngleRight,
  FaChevronDown,
  FaEnvelope,
  FaFacebookF,
  FaFlask,
  FaGraduationCap,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegClipboard,
  FaSearch,
  FaShieldAlt,
  FaUniversity,
  FaUserGraduate,
  FaYoutube,
} from "react-icons/fa";
import { NavLink } from "react-router-dom";
import languages from "../../localization";
import { AuthContext } from "../../authContext";
import useStore from "../../store/store";
import LOGO from "../../Assets/logo.png";
import ContactAddress from "../../Components/ContactAddress";

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(query.matches);
    update();

    if (query.addEventListener) query.addEventListener("change", update);
    else query.addListener(update);

    return () => {
      if (query.removeEventListener) query.removeEventListener("change", update);
      else query.removeListener(update);
    };
  }, []);

  return prefersReducedMotion;
}

function useCompactFooter() {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia("(max-width: 720px)");
    const update = () => setIsCompact(query.matches);
    update();

    if (query.addEventListener) query.addEventListener("change", update);
    else query.addListener(update);

    return () => {
      if (query.removeEventListener) query.removeEventListener("change", update);
      else query.removeListener(update);
    };
  }, []);

  return isCompact;
}

function FooterNetworkCanvas({ reducedMotion }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (reducedMotion || typeof window === "undefined" || !window.IntersectionObserver) return undefined;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d", { alpha: true });
    if (!canvas || !context) return undefined;

    let frameId = 0;
    let running = false;
    let isVisible = false;
    let width = 0;
    let height = 0;
    let nodes = [];

    const createNodes = () => {
      const count = width < 720 ? 12 : width < 1200 ? 18 : 24;
      nodes = Array.from({ length: count }, (_, index) => ({
        x: 0.06 + ((index * 0.7213) % 1) * 0.88,
        y: 0.12 + (((index * 29) % count) / count) * 0.72,
        phase: index * 0.61,
        speed: 0.1 + (index % 4) * 0.015,
        radius: 1.2 + (index % 3) * 0.35,
        px: 0,
        py: 0,
      }));
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 1.35);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      createNodes();
    };

    const draw = (timestamp = 0) => {
      if (!running) return;
      const time = timestamp * 0.001;
      context.clearRect(0, 0, width, height);

      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        node.px = node.x * width + Math.cos(time * node.speed + node.phase) * 10;
        node.py = node.y * height + Math.sin(time * node.speed * 1.35 + node.phase) * 8;
      }

      context.lineWidth = 1;
      for (let i = 0; i < nodes.length; i += 1) {
        const left = nodes[i];
        for (let j = i + 1; j < nodes.length; j += 1) {
          const right = nodes[j];
          const dx = left.px - right.px;
          const dy = left.py - right.py;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = width < 720 ? 86 : 132;
          if (distance >= maxDistance) continue;
          context.strokeStyle = `rgba(174, 228, 223, ${0.1 * (1 - distance / maxDistance)})`;
          context.beginPath();
          context.moveTo(left.px, left.py);
          context.lineTo(right.px, right.py);
          context.stroke();
        }
      }

      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        context.fillStyle = i % 5 === 0 ? "rgba(213, 161, 30, 0.36)" : "rgba(174, 228, 223, 0.26)";
        context.beginPath();
        context.arc(node.px, node.py, node.radius, 0, Math.PI * 2);
        context.fill();
      }

      frameId = window.requestAnimationFrame(draw);
    };

    const start = () => {
      if (running) return;
      running = true;
      frameId = window.requestAnimationFrame(draw);
    };

    const stop = () => {
      running = false;
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = 0;
    };

    const handleVisibility = () => {
      if (document.hidden || !isVisible) stop();
      else start();
    };

    resize();
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) start();
        else stop();
      },
      { threshold: 0.08 }
    );

    observer.observe(canvas);
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [reducedMotion]);

  return (
    <div className={styles.canvasLayer} aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}

const cleanLinks = (links) => links.filter((item) => item?.label && item?.to);

function FooterContactItem({ icon, label, value, href, external, richText = false }) {
  if (!value) return null;
  const content = (
    <>
      <i className={styles.contactIcon}>{icon}</i>
      <div className={styles.contactCopy}>
        <small>{label}</small>
        {richText ? <ContactAddress html={value} className={styles.contactAddress} /> : <strong>{value}</strong>}
      </div>
    </>
  );

  if (href) {
    return (
      <a className={styles.contactItem} href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>
        {content}
      </a>
    );
  }

  return <div className={styles.contactItem}>{content}</div>;
}

const Footer = ({ className = "", dir, id = "site-footer", ...rest }) => {
  const [globalState] = useStore();
  const { setLanguage, languageCode } = useContext(AuthContext);
  const contactinfo = (globalState.contactinfos || [])[0] || {};
  const lang = languages.normalizeLanguage(languageCode || languages.getLanguage());
  const year = new Date().getFullYear();
  const prefersReducedMotion = usePrefersReducedMotion();
  const isCompactFooter = useCompactFooter();
  const footerRef = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const direction = dir || languages.getLanguageDirection(lang);
  const address = languages.getLocalizedField(contactinfo, "address", lang);

  useEffect(() => {
    const node = footerRef.current;
    if (!node) return undefined;
    if (prefersReducedMotion || typeof window === "undefined" || !window.IntersectionObserver) {
      setIsRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setIsRevealed(true);
        observer.disconnect();
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  const socialLinks = [
    { href: contactinfo.facebook, label: "Facebook", icon: <FaFacebookF /> },
    { href: contactinfo.youtube, label: "YouTube", icon: <FaYoutube /> },
    { href: contactinfo.googleMap, label: languages.home_contact_detail || languages.contact, icon: <FaMapMarkedAlt /> },
  ].filter((item) => item.href);

  const linkGroups = [
      {
        title: languages.academics_programs || languages.academics,
        icon: <FaUniversity />,
        links: cleanLinks([
          { label: languages.academic_programs, to: "/about/academic_programs" },
          { label: languages.a_bcs_computer_science, to: "/cs/vission_mission" },
          { label: languages.a_bba_economics, to: "/eco/vission_mission" },
          { label: languages.academic_calendar, to: "/cs/academic_calendar" },
        ]),
      },
      {
        title: languages.home_admission_eyebrow || languages.online_admission,
        icon: <FaRegClipboard />,
        links: cleanLinks([
          { label: languages.home_apply_now || languages.online_admission, to: "/online-registration" },
          { label: languages.kankor_results || languages.result, to: "/kankor/results" },
          { label: languages.kankor_guide, to: "/kankor/kankor_guide" },
          { label: languages.offline_enrollment_requirement, to: "/kankor/offline_enrollment_requirement" },
        ]),
      },
      {
        title: languages.student_services || languages.student,
        icon: <FaUserGraduate />,
        links: cleanLinks([
          { label: languages.scholarships_financing, to: "/students/scholarships_financing" },
          { label: languages.eligibility_criteria, to: "/students/eligibility_criteria" },
        ]),
      },
      {
        title: languages.research,
        icon: <FaFlask />,
        links: cleanLinks([
          { label: languages.research_guide, to: "/research/research_guide" },
          { label: languages.published_researche, to: "/research/published_researche" },
          { label: languages.research_paper, to: "/research/research_paper" },
          { label: languages.research_trainings, to: "/research/research_trainings" },
        ]),
      },
  ];

  const languageOptions = [
      { code: "ps", label: "\u067e\u069a\u062a\u0648", short: "پښتو" },
      { code: "fa", label: "\u062f\u0631\u06cc", short: "دری" },
      { code: "en", label: "English", short: "English" },
    
  ];

  
  const handleSummaryClick = (event) => {
    if (!isCompactFooter) event.preventDefault();
  };

  return (
    <footer
      ref={footerRef}
      id={id}
      className={[styles.footer, isRevealed ? styles.isVisible : "", className].filter(Boolean).join(" ")}
      dir={direction}
      aria-label={languages.saba_university}
      {...rest}
    >
      <FooterNetworkCanvas reducedMotion={prefersReducedMotion} />

      <div className={styles.footerShell}>
        <div className={[styles.footerContent, "w-controller"].join(" ")}>
          <section className={styles.identityPanel} aria-label={languages.saba_university}>
            <NavLink to="/" className={styles.brandLine}>
              <span className={styles.logoFrame}>
                <img src={LOGO} alt={languages.saba_university} />
              </span>
              <span>
                <strong>{languages.saba_university}</strong>
                <small>{languages.home_hero_eyebrow}</small>
              </span>
            </NavLink>

            <p className={styles.footerLead}>{languages.footer_about_text}</p>

            <div className={styles.trustRow}>
              <span>
                <FaShieldAlt aria-hidden="true" />
                {languages.official}
              </span>
              <span>
                <FaGraduationCap aria-hidden="true" />
                {languages.home_stat_faculties || languages.faculties}
              </span>
            </div>

            {socialLinks.length > 0 && (
              <div className={styles.socialBlock}>
                <span>{languages.contact}</span>
                <div className={styles.socialLinks}>
                  {socialLinks.map((item) => (
                    <a href={item.href} target="_blank" rel="noreferrer" aria-label={item.label} title={item.label} key={item.label}>
                      {item.icon}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className={styles.contactPanel} aria-label={languages.contact}>
            <div className={styles.panelTitle}>
              <span>{languages.home_contact_eyebrow || languages.contact}</span>
              <h2>{languages.contact}</h2>
            </div>

            <div className={styles.contactGrid}>
              <FooterContactItem icon={<FaMapMarkerAlt />} label={languages.home_contact_detail || languages.contact} value={address} href={contactinfo.googleMap} external richText />
              <FooterContactItem icon={<FaEnvelope />} label={languages.email} value={contactinfo.email} href={contactinfo.email ? `mailto:${contactinfo.email}` : ""} />
              <FooterContactItem icon={<FaPhoneAlt />} label={languages.phone} value={contactinfo.phone} href={contactinfo.phone ? `tel:${contactinfo.phone}` : ""} />
            </div>
          </section>

          <section className={styles.linksPanel} aria-label={languages.footer_links_title}>
            <div className={styles.portalStrip}>
              <div>
                <span>{languages.kankor_result_portal || languages.official}</span>
                <strong>{languages.home_quick_links_title || languages.footer_links_title}</strong>
              </div>
              <div className={styles.portalActions}>
                <NavLink to="/online-registration">
                  <FaRegClipboard aria-hidden="true" />
                  {languages.home_apply_now || languages.online_admission}
                </NavLink>
                <NavLink to="/kankor/results">
                  <FaSearch aria-hidden="true" />
                  {languages.kankor_results || languages.result}
                </NavLink>
              </div>
            </div>

            <nav className={styles.linkGroups} aria-label={languages.footer_links_title}>
              {linkGroups.map((group) => (
                <details className={styles.linkGroup} key={group.title} open={!isCompactFooter}>
                  <summary onClick={handleSummaryClick}>
                    <span>
                      <i>{group.icon}</i>
                      {group.title}
                    </span>
                    <FaChevronDown className={styles.groupChevron} aria-hidden="true" />
                  </summary>
                  <ul>
                    {group.links.map((item) => (
                      <li key={item.to}>
                        <NavLink to={item.to} className={styles.footerLink}>
                          <FaAngleRight className={styles.linkArrow} aria-hidden="true" />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </nav>
          </section>
        </div>

        <div className={styles.bottomBand}>
          <div className={[styles.footerBottom, "w-controller"].join(" ")}>
            <div className={styles.copyText}>
              <span>{languages.footer_copyright} {year} {languages.saba_university}</span>
              <small>{languages.home_hero_eyebrow}</small>
            </div>
            <div className={styles.languageSwitch} aria-label={languages.language_selector}>
              {languageOptions.map((item) => (
                <button
                  className={[styles.lang, lang === item.code ? styles.active : ""].join(" ")}
                  key={item.code}
                  onClick={() => setLanguage(item.code)}
                  type="button"
                  aria-pressed={lang === item.code}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
